import { useMemo } from 'react';
import { formatPrice } from '../../utils/formatPrice';
import type { ShippingTier } from '../../types/cart';

interface ShippingTierProgressProps {
  tiers: ShippingTier[];
  subtotal: number;
  currentTier: ShippingTier | null;
  nextTier: ShippingTier | null;
}

export function ShippingTierProgress({ tiers, subtotal, currentTier, nextTier }: ShippingTierProgressProps) {
  const sorted = useMemo(
    () => [...tiers].sort((a, b) => a.min_subtotal - b.min_subtotal),
    [tiers],
  );

  const firstTier = sorted[0];
  const minOrder = firstTier?.min_subtotal ?? 0;
  const isBelowMinimum = subtotal < minOrder;
  const isFreeShipping = currentTier?.shipping_cost === 0 && !nextTier;

  // Progress: below minimum → progress toward minimum; above → progress toward next tier
  const progress = useMemo(() => {
    if (isBelowMinimum) {
      return minOrder > 0 ? Math.max(0, (subtotal / minOrder) * 100) : 0;
    }
    if (!nextTier) return 100;
    const prevMin = currentTier?.min_subtotal ?? minOrder;
    const range = nextTier.min_subtotal - prevMin;
    if (range <= 0) return 100;
    return Math.min(100, Math.max(0, ((subtotal - prevMin) / range) * 100));
  }, [subtotal, currentTier, nextTier, isBelowMinimum, minOrder]);

  const remaining = isBelowMinimum
    ? minOrder - subtotal
    : nextTier ? nextTier.min_subtotal - subtotal : 0;

  return (
    <div className="mb-4">
      {/* Message */}
      <div className="text-xs mb-2">
        {isBelowMinimum ? (
          <div className="flex items-start gap-1.5 text-amber-700">
            <TruckIcon className="text-amber-500 mt-0.5" />
            <span>
              Agregá {formatPrice(remaining)} más para llegar al <strong>mínimo de {formatPrice(minOrder)}</strong>
            </span>
          </div>
        ) : isFreeShipping ? (
          <div className="flex items-center gap-1.5 text-green-700 font-medium">
            <TruckIcon className="text-green-600" />
            Envío gratis!
          </div>
        ) : nextTier ? (
          <div className="flex items-start gap-1.5 text-gray-600">
            <TruckIcon className="text-gray-400 mt-0.5" />
            <span>
              {nextTier.shipping_cost === 0 ? (
                <>Agregá {formatPrice(remaining)} más<br />y tu envío es <strong className="text-green-600">gratis</strong></>
              ) : (
                <>Agregá {formatPrice(remaining)} más<br />y tu envío baja a <strong>{formatPrice(nextTier.shipping_cost)}</strong></>
              )}
            </span>
          </div>
        ) : null}
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${
              isBelowMinimum ? 'bg-amber-400' : isFreeShipping ? 'bg-green-500' : 'bg-green-400'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Tier markers */}
        <div className="flex justify-between mt-1.5">
          {sorted.map((tier) => {
            const isActive = subtotal >= tier.min_subtotal;
            return (
              <div
                key={tier.min_subtotal}
                className="flex flex-col items-center"
              >
                <span className={`text-[10px] leading-tight ${isActive ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                  {tier.shipping_cost === 0 ? 'Gratis' : formatPrice(tier.shipping_cost)}
                </span>
                <span className={`text-[9px] leading-tight ${isActive ? 'text-green-600' : 'text-gray-300'}`}>
                  {formatPrice(tier.min_subtotal)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TruckIcon({ className }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className ?? ''}`}>
      <path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}
